import type { AnalyticsProvider } from '../types';
import { env } from '@/config/env';
import { getEffectiveParam } from '@/features/config';

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
  }
}

/**
 * Resolved at init time: the funnel config's `fb` param (priority 0) wins over
 * the build-time env id, so a config can retarget the pixel without a redeploy.
 */
let pixelId = '';

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Meta Pixel. We send only the pixel's own default events (PageView). Purchases
 * are reported server-side (gated by the `hide_fb_purchase_events` config
 * param); no other client-side events are forwarded to Facebook.
 */
export const pixelProvider: AnalyticsProvider = {
  name: 'meta-pixel',

  init() {
    pixelId = getEffectiveParam('fb')?.trim() || env.VITE_FB_PIXEL_ID || '';
    if (!pixelId) return;
    const n = ((...args: unknown[]) => {
      if ((n as any).callMethod) {
        (n as any).callMethod(...args);
      } else {
        (n as any).queue.push(args);
      }
    }) as any;
    if (!window._fbq) window._fbq = n;
    window.fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as unknown[];

    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(t);

    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
  },
  /* eslint-enable @typescript-eslint/no-explicit-any */

  track() {
    // Custom/app events are not forwarded to Facebook (purchases are server-side).
  },

  page() {
    if (!pixelId || typeof window.fbq !== 'function') return;
    window.fbq('track', 'PageView');
  },
};
